/**
 * Dialog Styles
 * Professional confirmation/alert dialog: solid card, circular icon, clear actions.
 * Theme-aware via getDialogStyles(theme).
 */

import { StyleSheet, Platform, Dimensions } from 'react-native';
import type { ThemeColors } from '../../../theme/themes';
import { makeBoxShadow } from '../../helpers/shadowStyles.helper';
import { WEB_NATIVE_APP_PROMO_MASCOT_LAYOUT } from '../../constants/webNativeAppPromo.constants';

const { width: SCREEN_WIDTH } = Dimensions.get('window');
const MAX_DIALOG_WIDTH = 400;

const ICON_CIRCLE_SIZE = 56;
const ICON_SIZE = 28;

function hexToRgba(hex: string, alpha: number): string {
  const h = String(hex).replace(/^#/, '');
  if (h.length === 6) {
    const r = parseInt(h.slice(0, 2), 16);
    const g = parseInt(h.slice(2, 4), 16);
    const b = parseInt(h.slice(4, 6), 16);
    return `rgba(${r},${g},${b},${alpha})`;
  }
  return `rgba(0,0,0,${alpha})`;
}

export function getDialogStyles(theme: ThemeColors) {
  const dialogContent = {
    width: '100%' as const,
    borderRadius: 16,
    padding: 24,
    alignItems: 'center' as const,
    backgroundColor: theme.background.card,
    borderWidth: 1,
    borderColor: theme.gray[200],
    ...(Platform.OS === 'web'
      ? {
          boxShadow: `0 4px 24px ${theme.shadow}, 0 0 1px ${theme.gray[200]}`,
        }
      : {
          ...makeBoxShadow({
            shadowColor: theme.shadow,
            shadowOffset: { width: 0, height: 4 },
            shadowOpacity: 0.15,
            shadowRadius: 24,
          }),
          elevation: 8,
        }),
  };

  return StyleSheet.create({
    backdrop: {
      flex: 1,
      justifyContent: 'center',
      alignItems: 'center',
      ...(Platform.OS === 'web' && {
        position: 'fixed' as never,
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        width: '100%',
        height: '100%',
        zIndex: 10000,
        pointerEvents: 'auto' as never,
      }),
    },
    backdropOverlay: {
      flex: 1,
      width: '100%',
      height: '100%',
      justifyContent: 'center',
      alignItems: 'center',
      backgroundColor: theme.background.overlay,
      pointerEvents: 'auto' as never,
    },
    dialogContainer: {
      width: SCREEN_WIDTH * 0.9,
      maxWidth: MAX_DIALOG_WIDTH,
      alignItems: 'center',
      justifyContent: 'center',
    },
    dialogContainerNativeAppPromo: {
      overflow: 'visible' as never,
    },
    dialogContent,
    dialogContentNativeAppPromo: {
      paddingTop: 12,
      paddingHorizontal: 24,
      paddingBottom: 24,
      zIndex: 1,
      ...(Platform.OS === 'web' && {
        overflow: 'visible' as never,
      }),
    },
    nativeAppPromoTitleSpacer: {
      width: '100%',
      height: WEB_NATIVE_APP_PROMO_MASCOT_LAYOUT.TITLE_TOP_SPACER_HEIGHT,
    },
    iconCircle: {
      width: ICON_CIRCLE_SIZE,
      height: ICON_CIRCLE_SIZE,
      borderRadius: ICON_CIRCLE_SIZE / 2,
      alignItems: 'center',
      justifyContent: 'center',
      marginBottom: 20,
    },
    iconCircleDanger: {
      backgroundColor: hexToRgba(theme.error, 0.12),
    },
    iconCircleDefault: {
      backgroundColor: hexToRgba(theme.info, 0.12),
    },
    iconCircleReward: {
      backgroundColor: hexToRgba(theme.accent, 0.2),
    },
    iconInner: {
      alignItems: 'center',
      justifyContent: 'center',
    },
    iconText: {
      fontSize: 26,
      textAlign: 'center',
    },
    title: {
      fontSize: 20,
      fontWeight: '700',
      textAlign: 'center',
      marginBottom: 8,
      color: theme.black,
    },
    message: {
      fontSize: 15,
      fontWeight: '400',
      textAlign: 'center',
      lineHeight: 22,
      marginBottom: 24,
      color: theme.gray[800],
    },
    buttonContainer: {
      flexDirection: 'row',
      gap: 12,
      width: '100%',
      justifyContent: 'center',
      alignItems: 'center',
    },
    button: {
      flex: 1,
      paddingVertical: 14,
      paddingHorizontal: 20,
      borderRadius: 12,
      alignItems: 'center',
      justifyContent: 'center',
      minHeight: 48,
    },
    buttonPressed: {
      opacity: 0.7,
    },
    buttonHover: {
      opacity: 0.9,
    },
    primaryButton: {
      // base; use primaryButtonDanger / primaryButtonDefault / primaryButtonReward for fill
    },
    primaryButtonDanger: {
      backgroundColor: theme.error,
    },
    primaryButtonDefault: {
      backgroundColor: theme.info,
    },
    primaryButtonReward: {
      backgroundColor: theme.accent,
    },
    secondaryButton: {
      borderWidth: 1,
      backgroundColor: theme.background.card,
      borderColor: theme.gray[300],
    },
    destructiveSecondaryButton: {
      borderWidth: 1,
      backgroundColor: theme.background.card,
      borderColor: theme.error,
    },
    buttonText: {
      fontSize: 16,
      fontWeight: '600',
    },
    primaryButtonText: {
      color: theme.white,
    },
    secondaryButtonText: {
      color: theme.gray[800],
    },
    destructiveSecondaryButtonText: {
      color: theme.error,
    },

    deleteAccountHint: {
      fontSize: 13,
      fontWeight: '400' as const,
      textAlign: 'center' as const,
      lineHeight: 18,
      marginBottom: 8,
      color: theme.gray[600],
      width: '100%',
    },
    deleteAccountInput: {
      width: '100%',
      minHeight: 44,
      borderWidth: 1,
      borderColor: theme.gray[300],
      borderRadius: 10,
      paddingHorizontal: 12,
      paddingVertical: 10,
      fontSize: 15,
      color: theme.gray[900],
      marginBottom: 20,
      backgroundColor: theme.background.card,
    },
    primaryButtonDisabled: {
      opacity: 0.45,
    },
    nativeAppPromoMascotWrap: {
      alignItems: 'center',
      marginBottom: -WEB_NATIVE_APP_PROMO_MASCOT_LAYOUT.CARD_OVERLAP,
      zIndex: 2,
      ...(Platform.OS === 'android' && { elevation: 4 }),
      ...(Platform.OS === 'web' && {
        overflow: 'visible' as never,
      }),
    },
    nativeAppPromoMascotImage: {
      width: WEB_NATIVE_APP_PROMO_MASCOT_LAYOUT.WIDTH,
      height: WEB_NATIVE_APP_PROMO_MASCOT_LAYOUT.HEIGHT,
      /** marginTop — transform translateY is dropped on Storybook <img> (RN array → no CSS). */
      marginTop: WEB_NATIVE_APP_PROMO_MASCOT_LAYOUT.TRANSLATE_Y,
    },
    nativeAppPromoCheckboxRow: {
      flexDirection: 'row',
      alignItems: 'center',
      alignSelf: 'flex-start',
      width: '100%',
      marginBottom: 20,
      gap: 10,
    },
    nativeAppPromoCheckboxBox: {
      width: 22,
      height: 22,
      borderRadius: 6,
      borderWidth: 2,
      borderColor: theme.gray[400],
      alignItems: 'center',
      justifyContent: 'center',
      backgroundColor: theme.background.card,
    },
    nativeAppPromoCheckboxBoxChecked: {
      borderColor: theme.info,
      backgroundColor: hexToRgba(theme.info, 0.12),
    },
    nativeAppPromoCheckboxLabel: {
      flex: 1,
      fontSize: 14,
      fontWeight: '500',
      color: theme.gray[800],
      textAlign: 'left',
    },
  });
}

export const ICON_CIRCLE_SIZE_EXPORT = ICON_CIRCLE_SIZE;
export const ICON_SIZE_EXPORT = ICON_SIZE;
